import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  compareItemCode,
  flattenItemTree as flattenItems,
  getItemLevel,
  hasChildItems as isParentItem,
} from "../subcontract-items/itemHierarchy";

const FONT_NAME = "Times New Roman";
const MONEY_FORMAT = "#,##0";
const QUANTITY_FORMAT = "#,##0.####";

const SECTION_SUFFIX = {
  ESTIMATE: "DuToan",
  COMMUNE_DETAIL: "ChiTietMoiXa",
  PROVINCE_COMMUNE: "PhanCapTinhXa",
};

const COLUMNS = [
  { key: "itemCode", header: "TT", width: 7.13 },
  { key: "itemName", header: "Nội dung công việc", width: 26.63 },
  { key: "unit", header: "ĐVT", width: 11.25 },
  { key: "difficultyLevel", header: "Mức KK", width: 11.13 },
  { key: "laborCount", header: "Định biên", width: 11.13 },
  { key: "laborNorm", header: "Định mức", width: 13.88 },
  { key: "laborPrice", header: "Đơn giá công (đồng)", width: 14.75 },
  { key: "productPrice", header: "Đơn giá sản phẩm", width: 14.75 },
  { key: "subcontractQuantity", header: "Khối lượng", width: 11.88 },
  { key: "subcontractAmount", header: "Thành tiền (đồng)", width: 13.88 },
  { key: "notes", header: "Ghi chú", width: 10.88 },
];

const thinBorder = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
};

const getValue = (source, key, fallback = "") =>
  source?.[key] ?? source?.[`${key[0].toUpperCase()}${key.slice(1)}`] ?? fallback;

const normalize = (value) => String(value || "").trim().toUpperCase();

const safeFileName = (value) =>
  (value || "phieu-giao-khoan")
    .toString()
    .trim()
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 120);

const sheetName = (value, fallback = "Phieu giao khoan") =>
  (value || fallback).toString().replace(/[\\/?*[\]:]/g, "-").slice(0, 31) || fallback;

const uniqueSheetName = (workbook, value, fallback = "Phieu giao khoan") => {
  const baseName = sheetName(value, fallback);
  let candidate = baseName;
  let index = 2;

  while (workbook.getWorksheet(candidate)) {
    const suffix = ` (${index})`;
    candidate = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
    index += 1;
  }

  return candidate;
};

const getScopeName = (scope) => getValue(scope, "scopeName", getValue(scope, "name", "Phiếu giao khoán"));

const getScopeType = (scope) => normalize(getValue(scope, "scopeType"));

const getExportScopes = (section) => {
  const scopes = section.scopes || section.Scopes || [];

  return scopes.filter((scope) => {
    const sectionType = normalize(getValue(section, "sectionType"));
    const scopeType = getScopeType(scope);
    return !(sectionType === "COMMUNE_DETAIL" && scopeType === "TOTAL" && (scope.items || scope.Items || []).length === 0);
  });
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const getScopeId = (item) => getValue(item, "subcontractItemScopeId", getValue(item, "scopeId", null));

const sortItems = (items = []) => flattenItems(items).sort(compareItemCode);

const getScopeItems = (scope, fallbackItems = []) => {
  const scopeItems = scope?.items || scope?.Items || [];
  if (scopeItems.length > 0) return sortItems(scopeItems);

  const scopeId = getValue(scope, "subcontractItemScopeId", getValue(scope, "id", null));
  return sortItems(fallbackItems.filter((item) => String(getScopeId(item)) === String(scopeId)));
};

const getSectionsForExport = (sections = [], items = []) => {
  if (sections.length > 0) return sections;

  return [{
    sectionName: "Phiếu giao khoán",
    sectionType: "ASSIGNMENT_FORM",
    scopes: [{
      scopeName: "Phiếu giao khoán",
      scopeType: "DEFAULT",
      items,
    }],
  }];
};

const getRoundedAssignmentTotal = (items = []) =>
  Math.round(
    items
      .filter((item) => !isParentItem(item))
      .reduce((total, item) => {
        const laborCount = numericValue(getValue(item, "laborCount")) || 0;
        const laborNorm = numericValue(getValue(item, "laborNorm")) || 0;
        const laborPrice = numericValue(getValue(item, "laborPrice")) || 0;
        const quantity = numericValue(getValue(item, "subcontractQuantity")) || 0;
        return total + laborCount * laborNorm * laborPrice * quantity;
      }, 0) / 1000,
  ) * 1000;

const VIETNAMESE_DIGITS = [
  "không",
  "một",
  "hai",
  "ba",
  "bốn",
  "năm",
  "sáu",
  "bảy",
  "tám",
  "chín",
];

const readTriple = (value, full = false) => {
  const hundreds = Math.floor(value / 100);
  const tens = Math.floor((value % 100) / 10);
  const ones = value % 10;
  const parts = [];

  if (hundreds > 0 || full) {
    parts.push(`${VIETNAMESE_DIGITS[hundreds]} trăm`);
    if (tens === 0 && ones > 0) parts.push("lẻ");
  }

  if (tens > 1) {
    parts.push(`${VIETNAMESE_DIGITS[tens]} mươi`);
    if (ones === 1) parts.push("mốt");
    else if (ones === 5) parts.push("lăm");
    else if (ones > 0) parts.push(VIETNAMESE_DIGITS[ones]);
  } else if (tens === 1) {
    parts.push("mười");
    if (ones === 5) parts.push("lăm");
    else if (ones > 0) parts.push(VIETNAMESE_DIGITS[ones]);
  } else if (ones > 0) {
    parts.push(VIETNAMESE_DIGITS[ones]);
  }

  return parts.join(" ");
};

const numberToVietnameseWords = (value) => {
  const numberValue = Math.round(Number(value || 0));
  if (!numberValue) return "Không";

  const units = ["", "nghìn", "triệu", "tỷ"];
  const groups = [];
  let remaining = numberValue;

  while (remaining > 0) {
    groups.push(remaining % 1000);
    remaining = Math.floor(remaining / 1000);
  }

  const words = [];
  for (let index = groups.length - 1; index >= 0; index -= 1) {
    const groupValue = groups[index];
    if (!groupValue) continue;

    const full = index < groups.length - 1 && groupValue < 100;
    words.push(readTriple(groupValue, full));
    if (units[index]) words.push(units[index]);
  }

  const result = words.join(" ").replace(/\s+/g, " ").trim();
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const setCell = (worksheet, address, value, style = {}) => {
  const cell = worksheet.getCell(address);
  cell.value = value;
  cell.font = { name: FONT_NAME, size: 13, ...(style.font || {}) };
  cell.alignment = { vertical: "middle", wrapText: true, ...(style.alignment || {}) };
  if (style.border) cell.border = style.border;
  if (style.fill) cell.fill = style.fill;
  if (style.numFmt) cell.numFmt = style.numFmt;
  return cell;
};

const merge = (worksheet, range) => {
  if (!worksheet.getCell(range.split(":")[0]).isMerged) {
    worksheet.mergeCells(range);
  }
};

const styleRange = (worksheet, startRow, endRow, startCol, endCol, style = {}) => {
  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
    const row = worksheet.getRow(rowIndex);
    for (let colIndex = startCol; colIndex <= endCol; colIndex += 1) {
      const cell = row.getCell(colIndex);
      cell.font = { name: FONT_NAME, size: 12, ...(style.font || {}) };
      cell.alignment = { vertical: "middle", wrapText: true, ...(style.alignment || {}) };
      if (style.border) cell.border = style.border;
      if (style.fill) cell.fill = style.fill;
      if (style.numFmt) cell.numFmt = style.numFmt;
    }
  }
};

const addHeader = (worksheet, { subcontract, section, scope }) => {
  merge(worksheet, "A1:C1");
  merge(worksheet, "E1:K1");
  merge(worksheet, "A2:C2");
  merge(worksheet, "E2:K2");
  merge(worksheet, "E4:K4");
  merge(worksheet, "A5:K5");
  merge(worksheet, "A6:K6");
  merge(worksheet, "A7:K7");
  merge(worksheet, "A8:K8");
  merge(worksheet, "A9:K9");
  merge(worksheet, "B10:J10");
  merge(worksheet, "B11:J11");
  merge(worksheet, "B12:K12");
  merge(worksheet, "B13:K13");
  merge(worksheet, "A14:K14");
  merge(worksheet, "B15:K15");
  merge(worksheet, "B17:G17");
  merge(worksheet, "B18:H18");
  merge(worksheet, "B19:H19");

  const issueDate = new Date(getValue(subcontract, "issueDate", Date.now()));
  const dateText = `Ngày ${issueDate.getDate()} tháng ${issueDate.getMonth() + 1} năm ${issueDate.getFullYear()}`;
  const subcontractNumber = getValue(subcontract, "subcontractNumber", getValue(subcontract, "code", "…./PGK-XN"));
  const subcontractName = getValue(subcontract, "subcontractName", getValue(subcontract, "name", ""));
  const sectionName = getValue(section, "sectionName", getValue(section, "name", ""));
  const scopeName = getValue(scope, "scopeName", getValue(scope, "name", ""));
  const assigneeName =
    getValue(subcontract?.assignee || {}, "assigneeName") ||
    getValue(subcontract, "subcontractorName") ||
    "…..............................";

  setCell(worksheet, "A1", "CÔNG TY TNHH MTV TĐBĐ", {
    font: { size: 16 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "E1", "CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM", {
    font: { bold: true, size: 16 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A2", "XÍ NGHIỆP TRẮC ĐỊA", {
    font: { size: 14 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "E2", "Độc lập - Tự do - Hạnh phúc", {
    font: { bold: true, size: 15 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "E4", dateText, {
    font: { italic: true },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A5", "PHIẾU GIAO KHOÁN CÔNG VIỆC", {
    font: { bold: true, size: 16 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A6", `Số: ${`${subcontractNumber}/PGK-XN` || "…./PGK-XN"}`, {
    font: { bold: true, size: 14 },
    alignment: { horizontal: "center" },
  });
  // setCell(worksheet, "A7", `Hạng mục công việc: ${sectionName || scopeName || "……………….."}`, {
  setCell(worksheet, "A7", `Hạng mục công việc: ${"……………….."}`, {
    font: { size: 14 },
    alignment: { horizontal: "center" },
  });
  // setCell(worksheet, "A8", `Công trình (Công đoạn sản phẩm): ${subcontractName || "……………………."}`, {
  setCell(worksheet, "A8", `Công trình (Công đoạn sản phẩm): ${"……………………."}`, {
    font: { size: 14 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A9", "GIÁM ĐỐC XÍ NGHIỆP, CHI NHÁNH …..", {
    font: { bold: true, size: 14 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A10", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B10", "Căn cứ chức năng quyền hạn và nhiệm vụ của Giám đốc Xí nghiệp ……;");
  setCell(worksheet, "A11", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B11", "Căn cứ quyết định giao nhiệm vụ số: …, ngày …/…./202…;");
  setCell(worksheet, "A12", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B12", "Căn cứ Bộ định mức lao động công nghệ của Công ty TNHH MTV Trắc địa Bản đồ ban hành kèm theo QĐ số …., ngày .../.../….", {
    font: { size: 12 },
  });
  setCell(worksheet, "A13", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B13", "Căn cứ …….", { font: { size: 12 } });
  setCell(worksheet, "A14", "QUYẾT ĐỊNH", {
    font: { bold: true, size: 15 },
    alignment: { horizontal: "center" },
  });
  setCell(worksheet, "A15", "I-", { font: { bold: true } });
  setCell(
    worksheet,
    "B15",
    `Giao khoán công việc cho bộ phận sản xuất: Do đ/c: ${assigneeName} Đội trưởng(QĐPX/ CNCT/PT. Bộ phận)`,
    { font: { bold: true } },
  );
  setCell(worksheet, "A16", "II-", { font: { bold: true } });
  setCell(worksheet, "B16", "Nội dung công việc, chi phí sản xuất:", { font: { bold: true } });
  setCell(worksheet, "A17", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B17", "Đơn giá ngày công ngoại nghiệp:…………….", { font: { bold: false } });
  setCell(worksheet, "J17", "đồng/công", { font: { bold: false } });
  setCell(worksheet, "A18", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B18", "Đơn giá ngày công nội nghiệp:……………….", { font: { bold: false } });
  setCell(worksheet, "J18", "đồng/công", { font: { bold: false } });
  setCell(worksheet, "A19", "-", { alignment: { horizontal: "center" } });
  setCell(worksheet, "B19", "Đơn giá ngày công lao động phổ thông:…………", { font: { bold: false } });
  setCell(worksheet, "J19", "đồng/công", { font: { bold: false } });

  [1, 2, 4, 5, 6, 7, 8, 9, 14, 15, 16].forEach((rowNumber) => {
    worksheet.getRow(rowNumber).height = rowNumber === 15 ? 49.5 : undefined;
  });
};

const addTableHeader = (worksheet) => {
  const headerRow = worksheet.getRow(20);
  COLUMNS.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = column.header;
  });
  const noteRow = worksheet.getRow(21);
  [
    "(1)",
    "(2)",
    "(3)",
    "(4)",
    "(5)",
    "(6)",
    "(7)",
    "(8=5*6*7)",
    "(9)",
    "(10=8*9)",
    "(11)",
  ].forEach((value, index) => {
    noteRow.getCell(index + 1).value = value;
  });

  worksheet.getRow(20).height = 31.5;
  worksheet.getRow(21).height = 20.25;
  styleRange(worksheet, 20, 21, 1, 11, {
    border: thinBorder,
    font: { bold: true },
    alignment: { horizontal: "center", vertical: "middle", wrapText: true },
  });
};

const addSectionRow = (worksheet, rowNumber, code, label) => {
  const row = worksheet.getRow(rowNumber);
  row.getCell(1).value = code;
  row.getCell(2).value = label;
  row.height = 24;
  styleRange(worksheet, rowNumber, rowNumber, 1, 11, {
    border: thinBorder,
    font: { bold: true },
    alignment: { vertical: "middle", wrapText: true },
  });
};

const addItemRow = (worksheet, rowNumber, item) => {
  const isParent = isParentItem(item);
  const laborCount = numericValue(getValue(item, "laborCount"));
  const laborNorm = numericValue(getValue(item, "laborNorm"));
  const laborPrice = numericValue(getValue(item, "laborPrice"));
  const quantity = numericValue(getValue(item, "subcontractQuantity"));

  const row = worksheet.getRow(rowNumber);
  row.height = 24;
  row.values = [
    getValue(item, "itemCode"),
    getValue(item, "itemName"),
    isParent ? "" : getValue(item, "unit"),
    isParent ? "" : numericValue(getValue(item, "difficultyLevel")),
    isParent ? "" : laborCount,
    isParent ? "" : laborNorm,
    isParent ? "" : laborPrice,
    isParent ? "" : { formula: `IF(OR(E${rowNumber}="",F${rowNumber}="",G${rowNumber}=""),"",E${rowNumber}*F${rowNumber}*G${rowNumber})` },
    isParent ? "" : quantity,
    isParent ? "" : { formula: `IF(OR(H${rowNumber}="",I${rowNumber}=""),"",H${rowNumber}*I${rowNumber})` },
    isParent ? "" : getValue(item, "notes"),
  ];

  styleRange(worksheet, rowNumber, rowNumber, 1, 11, {
    border: thinBorder,
    font: { bold: isParent },
    fill: isParent ? { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F4F6" } } : undefined,
    alignment: { vertical: "middle", wrapText: true },
  });

  row.getCell(1).alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  row.getCell(2).alignment = {
    horizontal: "left",
    vertical: "middle",
    wrapText: true,
    indent: Math.min(getItemLevel(item), 8),
  };
  [4, 5, 6, 7, 8, 9, 10].forEach((columnIndex) => {
    row.getCell(columnIndex).alignment = { horizontal: "right", vertical: "middle", wrapText: true };
  });
  [5, 6, 9].forEach((columnIndex) => {
    row.getCell(columnIndex).numFmt = QUANTITY_FORMAT;
  });
  [7, 8, 10].forEach((columnIndex) => {
    row.getCell(columnIndex).numFmt = MONEY_FORMAT;
  });
};

const addFooter = (worksheet, startRow, dataStartRow, dataEndRow, roundedTotal) => {
  let rowNumber = startRow;
  addSectionRow(worksheet, rowNumber, "II", "Vật tư, văn phòng phẩm");
  rowNumber += 1;
  setCell(worksheet, `B${rowNumber}`, "(Ghi chi tiết các hạng mục)", { border: thinBorder }, { font: { italic: true } });
  styleRange(worksheet, rowNumber, rowNumber, 1, 11, { border: thinBorder });
  rowNumber += 1;

  addSectionRow(worksheet, rowNumber, "III", "Chi phí khác");
  rowNumber += 1;
  setCell(worksheet, `B${rowNumber}`, "(Ghi chi tiết các hạng mục)", { border: thinBorder }, { font: { italic: true } });
  styleRange(worksheet, rowNumber, rowNumber, 1, 11, { border: thinBorder });
  rowNumber += 1;

  styleRange(worksheet, rowNumber, rowNumber, 1, 11, {
    border: thinBorder,
    font: { bold: true, size: 13 },
  });
  worksheet.getCell(rowNumber, 2).value = "Tổng cộng";
  worksheet.getCell(rowNumber, 10).value = { formula: `SUM(J${dataStartRow}:J${dataEndRow})` };
  worksheet.getCell(rowNumber, 10).numFmt = MONEY_FORMAT;
  const totalRow = rowNumber;
  rowNumber += 1;

  styleRange(worksheet, rowNumber, rowNumber, 1, 11, {
    border: thinBorder,
    font: { bold: true, size: 13 },
  });
  worksheet.getCell(rowNumber, 2).value = "Làm tròn số";
  worksheet.getCell(rowNumber, 10).value = { formula: `ROUND(J${totalRow},-3)` };
  worksheet.getCell(rowNumber, 10).numFmt = MONEY_FORMAT;
  rowNumber += 1;

  merge(worksheet, `A${rowNumber}:K${rowNumber}`);
  setCell(
    worksheet,
    `A${rowNumber}`,
    `Tổng số tiền bằng chữ: ${numberToVietnameseWords(roundedTotal)} đồng.`,
    {
      font: { bold: true, size: 14 },
      border: { top: { style: "thin" } },
      alignment: { horizontal: "left" },
    },
  );
  rowNumber += 1;

  setCell(worksheet, `A${rowNumber}`, "III -", { font: { bold: true } });
  setCell(worksheet, `B${rowNumber}`, "Điều kiện thanh toán:", { font: { bold: true } });
  rowNumber += 1;

  [
    "Sản phẩm được chấp nhận thanh toán sau khi có kết luận kiểm tra, thẩm định của cơ quan chức năng về khối lượng, chất lượng, sản phẩm giao nộp theo quy định. Bộ phận sản xuất chịu trách nhiệm sửa chữa, bổ sung hoàn thiện sản phẩm cho đến khi đơn vị thanh quyết toán với Chủ đầu tư.",
    "Tiền lương của các thành viên tham gia thi công theo đúng quy chế, quy định của Công ty.",
    "Chi phí khác được thanh toán theo thực tế (theo nội dung, khối lượng công việc thực hiện được kiểm tra, xác nhận của cơ quan chức năng và được Ban Giám đốc duyệt).",
    "……………………….",
  ].forEach((line) => {
    merge(worksheet, `B${rowNumber}:K${rowNumber}`);
    setCell(worksheet, `A${rowNumber}`, "-", { alignment: { horizontal: "center" } });
    setCell(worksheet, `B${rowNumber}`, line);
    worksheet.getRow(rowNumber).height = line.length > 120 ? 62.25 : 24;
    rowNumber += 1;
  });

  merge(worksheet, `A${rowNumber}:B${rowNumber}`);
  merge(worksheet, `C${rowNumber}:E${rowNumber}`);
  merge(worksheet, `F${rowNumber}:G${rowNumber}`);
  merge(worksheet, `H${rowNumber}:I${rowNumber}`);
  merge(worksheet, `J${rowNumber}:K${rowNumber}`);
  [
    ["A", "NGƯỜI NHẬN KHOÁN"],
    ["C", "PHỤ TRÁCH BỘ PHẬN"],
    ["F", "PHÒNG KHTH"],
    ["H", "PHÒNG TC-KT"],
    ["J", "GIÁM ĐỐC"],
  ].forEach(([column, label]) => {
    setCell(worksheet, `${column}${rowNumber}`, label, {
      font: { bold: true, size: 14 },
      alignment: { horizontal: "center" },
    });
  });
  rowNumber += 1;

  merge(worksheet, `A${rowNumber}:B${rowNumber}`);
  merge(worksheet, `C${rowNumber}:E${rowNumber}`);
  merge(worksheet, `F${rowNumber}:G${rowNumber}`);
  merge(worksheet, `H${rowNumber}:I${rowNumber}`);
  merge(worksheet, `J${rowNumber}:K${rowNumber}`);
  [
    ["A", "(Ký, ghi rõ họ tên)"],
    ["C", "(Ký, ghi rõ họ tên)"],
    ["F", "(Ký, ghi rõ họ tên)"],
    ["H", "(Ký, ghi rõ họ tên)"],
    ["J", "(Ký tên, đóng dấu)"],
  ].forEach(([column, label]) => {
    setCell(worksheet, `${column}${rowNumber}`, label, {
      font: { italic: true, size: 13 },
      alignment: { horizontal: "center" },
    });
  });
};

const addAssignmentWorksheet = ({ workbook, section, scope, subcontract, fallbackItems }) => {
  const items = getScopeItems(scope, fallbackItems);
  const worksheet = workbook.addWorksheet(uniqueSheetName(workbook, getScopeName(scope)));

  worksheet.properties.defaultRowHeight = 18;
  worksheet.pageSetup = {
    paperSize: 9,
    orientation: "portrait",
    fitToPage: true,
    fitToWidth: 1,
    fitToHeight: 0,
    margins: {
      left: 0.3,
      right: 0.3,
      top: 0.5,
      bottom: 0.5,
      header: 0.2,
      footer: 0.2,
    },
  };

  COLUMNS.forEach((column, index) => {
    worksheet.getColumn(index + 1).width = column.width;
  });

  addHeader(worksheet, { subcontract, section, scope });
  addTableHeader(worksheet);
  addSectionRow(worksheet, 22, "I", "Hạng mục công việc (Nhân công)");

  const dataStartRow = 23;
  const exportItems = items.length > 0 ? items : [{}];
  exportItems.forEach((item, index) => addItemRow(worksheet, dataStartRow + index, item));

  const dataEndRow = dataStartRow + exportItems.length - 1;
  addFooter(worksheet, dataEndRow + 1, dataStartRow, dataEndRow, getRoundedAssignmentTotal(items));
};

export const exportSubcontractAssignmentForm = async ({
  sections = [],
  subcontract = {},
  items = [],
  exportedBy = "",
} = {}) => {
  const exportSections = getSectionsForExport(sections, items);
  const subcontractName = getValue(subcontract, "subcontractName", getValue(subcontract, "name", ""));
  const subcontractCode = getValue(subcontract, "subcontractNumber", getValue(subcontract, "code", subcontractName));
  const suffix = new Date().toISOString().slice(0, 10);

  for (const section of exportSections) {
    const scopes = getExportScopes(section);
    if (scopes.length === 0) continue;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = exportedBy || "Project Trak";
    workbook.created = new Date();
    workbook.modified = new Date();
    workbook.calcProperties.fullCalcOnLoad = true;

    scopes.forEach((scope) => addAssignmentWorksheet({
      workbook,
      section,
      scope,
      subcontract,
      fallbackItems: items,
    }));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const sectionSuffix =
      SECTION_SUFFIX[normalize(getValue(section, "sectionType"))] ||
      safeFileName(getValue(section, "sectionName", getValue(section, "name", "PhieuGiaoKhoan")));

    saveAs(blob, `${safeFileName(subcontractCode || subcontractName)}_PhieuGiaoKhoan_${sectionSuffix}_${suffix}.xlsx`);
  }
};

export default exportSubcontractAssignmentForm;
